import React, { useEffect, useState } from "react";
import { devAutoFillAllForms, fillSpecificForm } from "@/utils/DevAutoFillAllForms";
import { PAGE_IDS } from '@/core/constants/pageIds';
import { faker } from '@faker-js/faker';

/**
 * Dev-only floating button that appears on pages containing at least one visible form input.
 * Uses existing faker-based auto fill logic.
 */
const DevFormAutofillButton: React.FC = () => {
    const [hasForms, setHasForms] = useState(false);
    const [lastResult, setLastResult] = useState<string>("");

    useEffect(() => {
        if (!import.meta.env.DEV) return; // only in dev

        const checkForms = () => {
            const forms = document.querySelectorAll("form");
            let visibleInputFound = false;
            forms.forEach(f => {
                const els = f.querySelectorAll("input, select, textarea");
                for (const el of Array.from(els)) {
                    const style = window.getComputedStyle(el as HTMLElement);
                    const hidden = (el as HTMLInputElement).type === "hidden";
                    if (!hidden && style.display !== "none" && style.visibility !== "hidden") {
                        visibleInputFound = true;
                        break;
                    }
                }
            });
            setHasForms(visibleInputFound);
        };

        checkForms();

        // React to DOM mutations (route changes / lazy loads)
        const observer = new MutationObserver(() => checkForms());
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    if (!import.meta.env.DEV || !hasForms) return null;

    const firePersonalEvent = () => {
        const personalFormPresent = document.querySelector("form [name='FirstName']") !== null;
        if (!personalFormPresent) return false;
        const payload = {
            FirstName: faker.person.firstName(),
            MiddleName: faker.person.middleName(),
            LastName: faker.person.lastName(),
            MaidenName: faker.person.lastName(),
            DateOfBirth: faker.date.birthdate({ min: 18, max: 55, mode: 'age' }).toISOString().substring(0,10),
            Gender: faker.helpers.arrayElement(['5','6','7','8']),
            MaritalStatus: faker.helpers.arrayElement(['Single','Married','Divorced']),
            HispanicOrLatino: faker.helpers.arrayElement(['Y','N']),
            RaceDescription: faker.helpers.arrayElement(['White','Black','Asian','Native American','Pacific Islander','Two or more']),
            USResidentStatus: faker.helpers.arrayElement(['1','2','6']),
            SSN: faker.string.numeric(9).replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3'),
            ConfirmSSN: faker.string.numeric(9).replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3'),
            VisaNumber: faker.string.alphanumeric(8).toUpperCase(),
            AlienNumber: faker.string.alphanumeric(9).toUpperCase(),
            CountryOfCitizenship: faker.location.country(),
            CountryOfBirth: faker.location.country(),
            phone: faker.phone.number({ style: 'national' }),
            mobileNumber: faker.phone.number({ style: 'national' })
        };
        window.dispatchEvent(new CustomEvent('dev:autoFillPersonal', { detail: { ...payload, pageId: PAGE_IDS.personal } }));
        // Also dispatch address info for forms that expect dedicated address autofill
        const addr = {
            Addr1: faker.location.streetAddress(),
            Addr2: faker.location.secondaryAddress(),
            City: faker.location.city(),
            State: faker.location.state({ abbreviated: true }),
            Zip: faker.location.zipCode(),
            Country: faker.location.country(),
            ShippingAddressStreet: faker.location.streetAddress(),
            ShippingAddressLine2: faker.location.secondaryAddress(),
            ShippingAddressCity: faker.location.city(),
            ShippingAddressState: faker.location.state({ abbreviated: true }),
            ShippingAddressPostalCode: faker.location.zipCode(),
            ShippingAddressCountry: faker.location.country(),
        };
        window.dispatchEvent(new CustomEvent('dev:autoFillAddress', { detail: { ...addr, pageId: PAGE_IDS.addressInfo } }));
        return true;
    };

    const handleClick = () => {
        const didPersonal = firePersonalEvent();

        // Wait briefly to let any event handlers (on the forms) update state
        // (for example AddressForm may set SameAsHome and mirror values)
        setTimeout(() => {
            // Try to find an active form to target
            let result = { filled: 0, skipped: 0 };
            try {
                // 0) Prefer form that matches current 'step' URL param (multi-step pages)
                const urlStep = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('step') : null;
                if (urlStep) {
                    const forms = Array.from(document.querySelectorAll<HTMLFormElement>('form'));
                    const pageGuid = (PAGE_IDS as Record<string,string>)[urlStep as string];
                    const matched = forms.find(f => {
                        const formPageId = f.getAttribute('data-page-id');
                        const dataForm = f.getAttribute('data-form');
                        if (formPageId && pageGuid && formPageId === pageGuid) return true;
                        if (dataForm && (dataForm === urlStep || dataForm.toLowerCase() === urlStep.toLowerCase())) return true;
                        return false;
                    });
                    if (matched) {
                        result = fillSpecificForm(matched);
                        if (import.meta.env.DEV) console.debug('AutoFill: matched by urlStep ->', matched);
                        setLastResult(`Personal:${didPersonal ? 'ok' : 'n/a'} Filled ${result.filled} (skipped ${result.skipped})`);
                        setTimeout(() => setLastResult(""), 5000);
                        return;
                    }
                }

                // 1) Prefer a form inside the ProfilePage main container
                const mainContainer = document.querySelector('div.max-w-6xl');
                if (mainContainer) {
                    // prefer explicitly marked active form inside the container
                    const active = mainContainer.querySelector('form[data-active-form="true"]') as HTMLFormElement | null;
                    const targetForm = active ?? mainContainer.querySelector('form') as HTMLFormElement | null;
                    if (targetForm) {
                        result = fillSpecificForm(targetForm);
                        if (import.meta.env.DEV) console.debug('AutoFill: matched by mainContainer ->', targetForm);
                        setLastResult(`Personal:${didPersonal ? 'ok' : 'n/a'} Filled ${result.filled} (skipped ${result.skipped})`);
                        setTimeout(() => setLastResult(""), 5000);
                        return;
                    }
                }

                // 2) If focused element is inside a form, use that
                const active = document.activeElement as HTMLElement | null;
                if (active) {
                    const f = active.closest('form') as HTMLFormElement | null;
                    if (f) {
                        result = fillSpecificForm(f);
                        setLastResult(`Personal:${didPersonal ? 'ok' : 'n/a'} Filled ${result.filled} (skipped ${result.skipped})`);
                        setTimeout(() => setLastResult(""), 5000);
                        return;
                    }
                }

                // 3) Otherwise, find first visible form in the viewport
                const forms = Array.from(document.querySelectorAll<HTMLFormElement>('form'));
                const visibleForms = forms.filter(f => {
                    const els = f.querySelectorAll('input, select, textarea');
                    for (const el of Array.from(els)) {
                        const style = window.getComputedStyle(el as HTMLElement);
                        const hidden = (el as HTMLInputElement).type === 'hidden';
                        if (!hidden && style.display !== 'none' && style.visibility !== 'hidden') {
                            const rect = (f as HTMLElement).getBoundingClientRect();
                            // consider in-viewport first
                            if (rect.top < window.innerHeight && rect.bottom > 0) return true;
                            return true;
                        }
                    }
                    return false;
                });

                if (visibleForms.length) {
                    result = fillSpecificForm(visibleForms[0]);
                } else {
                    // fallback: fill all forms
                    result = devAutoFillAllForms();
                }
            } catch {
                // fallback
                result = devAutoFillAllForms();
            }

            setLastResult(`Personal:${didPersonal ? 'ok' : 'n/a'} Filled ${result.filled} (skipped ${result.skipped})`);
            setTimeout(() => setLastResult(""), 5000);
        }, 120); // small delay to allow form handlers to run
    };

    return (
        <div className="z-[9998] fixed bottom-4 right-4 flex flex-col items-end gap-2">
            <button
                type="button"
                onClick={handleClick}
                className="bg-ecpi-blue text-white rounded-lg px-4 py-2 text-xs font-semibold shadow-lg hover:bg-ecpi-blue/90 focus:ring-ecpi-blue/40 focus:outline-none focus:ring-2"
                title="Auto-fill all visible form fields with fake data"
            >
                Auto-Fill Form
            </button>
            {lastResult && (
                <div className="bg-slate-900/90 text-emerald-300 border-slate-700 max-w-56 rounded border px-2 py-1 font-mono text-[10px] shadow">
                    {lastResult}
                </div>
            )}
        </div>
    );
};

export default DevFormAutofillButton;
